import React, {
	Dispatch,
	SetStateAction,
	memo,
	useEffect,
	useState,
} from "react";
import {
	getPCDependentWaterFallDataOptimise,
	getWaterFallDataSouthDivision,
} from "../../../queries/ScenarioForecast";
import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	LabelList,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import {
	styled,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from "@mui/material";
import { numberFormatter } from "../../../utils/formatter";
import { group } from "console";
import useFilter from "../../../hooks/useFilter";

const CustomTooltip1 = ({ active, payload }: any) => {
	if (active && payload && payload.length) {
		let data = payload[0].payload;
		return (
			<div className="custom-tooltip bg-[#1a1916]">
				<p className="label p-0 m-0 text-lightWhite text-[10px] ">
					<label>Category: </label>
					<label className="font-bold">{data.groupName}</label>
				</p>
				<p className="label  p-0 m-0 text-lightWhite text-[10px]">
					<label>Label: </label>
					<label className="font-bold">{data.label}</label>
				</p>
				<p className="label  p-0 m-0 text-lightWhite text-[10px]">
					<label>Value: </label>
					<label className="font-bold">
						${numberFormatter(data.incrementFromStartIndex)}
					</label>
				</p>
			</div>
		);
	}
	return null;
};

type Props = {
	waterfallFilterValues: WaterFallFilterSelectedValues;
	setLoading: Dispatch<SetStateAction<number>>;
	isFilterExpanded: boolean;
};
const PricingMarginWaterfall = ({
	waterfallFilterValues,
	setLoading,
	isFilterExpanded,
}: Props) => {
	const [graphData, setGraphData] = useState<PricingMarginWaterfallGraph[]>();
	const [tableData, setTableData] = useState<PricingMarginWaterfallTable[]>(
		[]
	);
	const { selectedFilter } = useFilter();
	useEffect(() => {
		fetchGraphData();
	}, [waterfallFilterValues, selectedFilter]);

	const fetchGraphData = async () => {
		setLoading((prev) => prev + 1);
		try {
			const response = await Promise.all([
				getPCDependentWaterFallDataOptimise(
					{
						...waterfallFilterValues,

						project:
							waterfallFilterValues.project == "All"
								? undefined
								: waterfallFilterValues.project,
					},
					selectedFilter || undefined
				),
				getWaterFallDataSouthDivision(
					{
						...waterfallFilterValues,

						project:
							waterfallFilterValues.project == "All"
								? undefined
								: waterfallFilterValues.project,
					},
					selectedFilter || undefined
				),
			]);
			setTableData(response[1]?.data.data);
			setGraphData(response[0]?.data?.data ?? []);
		} catch (err) {
			console.log("error in get API --", err);
		} finally {
			setLoading((prev) => prev - 1);
		}
	};

	const renderCustomizedLabel = (props: any) => {
		let { x, y, width, height, value } = props;
		const radius = 10;

		y = y - radius;
		if (value < 0) {
			y = Math.abs(y + height - 5);
		}
		return (
			<text
				x={x + width / 2}
				y={y}
				fill="#000"
				textAnchor="middle"
				dominantBaseline="middle"
				className="text-[0.7vw]"
			>
				${numberFormatter(value)}
			</text>
		);
	};

	const headerStyle = {
		backgroundColor: "white",
		color: "black",
		border: "1px solid #bebebe",
		borderBottom: "2px solid rgb(117, 189, 8)",
	};
	const columnStyle = {
		color: "black",
		borderRight: "0.1px solid #bebebe",
		borderBottom: "0.1px solid #bebebe",
		innerHeight: "36px",
	};

	const groupMapTable = tableData.reduce((acc, item) => {
		if (!acc[item.groupName]) acc[item.groupName] = [];
		acc[item.groupName].push(item.label);
		return acc;
	}, {} as Record<string, string[]>);
	return (
		<div
			className={`border border-lightGrey p-[6px] bg-white mt-[1vh] flex flex-col justify-around ${isFilterExpanded ? "h-[58vh]" : "h-[66vh]"
				} `}
		>
			<div
				className={`w-full ${isFilterExpanded ? "h-[45%]" : "h-[50%]"}`}
			>
				<ResponsiveContainer width="100%" height="100%">
					<BarChart data={graphData}>
						{/* <CartesianGrid strokeWidth={1} stroke="#000" /> */}
						<XAxis tick={false} hide={true} />
						<ReferenceLine
							y={0}
							stroke="#ccc"
							strokeWidth={1}
							strokeDasharray="3 3"
						/>
						<YAxis
							tickMargin={0}
							domain={[
								(dataMin: any) =>
									Math.round(dataMin + dataMin * 0.1),
								(dataMax: any) =>
									Math.round(dataMax + dataMax * 0.2),
							]}
							ticks={[0]}
							tickFormatter={(value) => "$" + Math.round(value)}
							stroke="#000"
							fontSize={"13.16"}
						/>

						<Tooltip
							cursor={{ fill: "transparent" }}
							contentStyle={{
								color: "white",
								backgroundColor: "rgb(26, 25, 22)",
							}}
							content={<CustomTooltip1 />}
						/>
						<Bar
							dataKey="startingIndex"
							stackId="a"
							fill="transparent"
						/>
						<Bar
							dataKey="incrementFromStartIndex"
							stackId="a"
							fill="transparent"
							barSize={10}
						>
							<LabelList
								dataKey="incrementFromStartIndex"
								content={renderCustomizedLabel}
								position="top"
								stroke="#000"
								className="font-normal text-[0.7vw] fill-black font-sans text-black stroke-0"
							/>
							{graphData?.map((item, index) => (
								<Cell key={index} fill={item.fillColor} />
							))}
						</Bar>
					</BarChart>
				</ResponsiveContainer>
			</div>
			{tableData?.length !== 0 && (
				<TableContainer>
					<Table>
						<TableHead>
							<TableRow>
								<StyledTableCellData
									sx={{
										width: "3.5%",
										borderLeft: "none",
										borderTop: "none",
										borderBottom: "none",
										borderColor: "#bebebe",
									}}
								/>
								{Object.entries(groupMapTable).map(
									([group, labels]) => {
										return (
											<StyledTableCellData
												key={group}
												className="alignCenter table-header-text"
												colSpan={labels.length}
												sx={{
													width: "4%",
													...headerStyle,
												}}
											>
												{group}
											</StyledTableCellData>
										);
									}
								)}
							</TableRow>
							<TableRow>
								<StyledTableCellData
									sx={{
										width: "3.5%",
										borderLeft: "none",
										borderTop: "none",
										borderRight: "none",
										borderBottom:
											"2px solid rgb(117, 189, 8)",
									}}
									className="table-header-text"
								/>
								{tableData?.map((v, index) => (
									<StyledTableCellData
										key={v.OrderId}
										className="alignCenter table-header-text"
										sx={{ width: "4%", ...headerStyle }}
									>
										{v.label}
									</StyledTableCellData>
								))}
							</TableRow>
						</TableHead>
						<TableBody>
							<TableRow className="bg-[#86bc250f]">
								<StyledTableCellData
									className="alignCenter table-header-text"
									sx={{
										minWidth: "64px",
										...columnStyle,
										borderColor: "#bebebe",
									}}
								>
									Division Average
								</StyledTableCellData>
								{tableData?.map((v, index) => (
									<StyledTableCellData
										key={v.OrderId}
										className="alignCenter table-cell-text"
										sx={{
											width: "4%",
											...columnStyle,
										}}
									>
										{v?.value?.toFixed(2) ?? 0}%
									</StyledTableCellData>
								))}
							</TableRow>
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</div>
	);
};

const StyledTableCellData = styled(TableCell)({
	lineHeight: 1.5,
	border: "0.1px solid ",
	fontFamily:
		'BlinkMacSystemFont,-apple-system,"Segoe UI","Roboto","Oxygen","Ubuntu","Cantarell","Fira Sans","Droid Sans","Helvetica Neue","Helvetica","Arial",sans-serif;',
	height: "8vh",
	padding: 0,
	paddingLeft: "2px !important",
	paddingRight: "2px !important",
	paddingTop: "2px !important",
	paddingBottom: "2px !important",
});

export default memo(PricingMarginWaterfall);
